import { defineMessages, type MessageDescriptor } from 'dibs-react-intl';
import {
    type NftItemSearchAuctionStatus,
    type NftItemSearchAuctionMarketType,
    type NftItemSearchMediumType,
    type NftItemSearchSort,
} from '../../../nftMarket/components/__generated__/NftMarketGridRefetchQuery.graphql.ts';

export type {
    NftItemSearchAuctionStatus,
    NftItemSearchAuctionMarketType,
    NftItemSearchMediumType,
    NftItemSearchSort,
};

export const messages = defineMessages({
    all: {
        id: 'nft.market.filters.options.all',
        defaultMessage: 'All',
    },
    sold: {
        id: 'nft.market.filters.options.sold',
        defaultMessage: 'Sold',
    },
    liveAuction: {
        id: 'nft.market.filters.options.liveAuction',
        defaultMessage: 'Live Auction',
    },
    upcomingAuctions: {
        id: 'nft.market.filters.options.upcomingAuctions',
        defaultMessage: 'Upcoming',
    },
    primaryMarket: {
        id: 'nft.market.filters.options.primaryMarket',
        defaultMessage: 'Primary',
    },
    secondaryMarket: {
        id: 'nft.market.filters.options.secondaryMarket',
        defaultMessage: 'Secondary',
    },
    staticImage: {
        id: 'nft.market.filters.options.staticImage',
        defaultMessage: 'Static Image',
    },
    animatedGif: {
        id: 'nft.market.filters.options.animatedGif',
        defaultMessage: 'Animated GIF',
    },
    video: {
        id: 'nft.market.filters.options.video',
        defaultMessage: 'Video',
    },
    threeD: {
        id: 'nft.market.filters.options.threeD',
        defaultMessage: '3D',
    },
    html: {
        id: 'nft.market.filters.options.html',
        defaultMessage: 'HTML',
    },
    availability: {
        id: 'nft.market.filters.facet.availability',
        defaultMessage: 'Availability',
    },
    market: {
        id: 'nft.market.filters.facet.market',
        defaultMessage: 'Market',
    },
    medium: {
        id: 'nft.market.filters.facet.medium',
        defaultMessage: 'Medium',
    },
    sortBy: {
        id: 'nft.market.filters.sort.placeholder',
        defaultMessage: 'Sort by',
    },
    endingSoonest: {
        id: 'nft.market.filters.sort.endingSoonest',
        defaultMessage: 'Ending soonest',
    },
    newest: {
        id: 'nft.market.filters.sort.newest',
        defaultMessage: 'Newest',
    },
    priceHiToLo: {
        id: 'nft.market.filters.sort.priceHiToLo',
        defaultMessage: 'Price: High - Low',
    },
    priceLoToHi: {
        id: 'nft.market.filters.sort.priceLoToHi',
        defaultMessage: 'Price: Low - High',
    },
});

type AvailableFiltersValues = Extract<
    | NftItemSearchAuctionStatus
    | NftItemSearchAuctionMarketType
    | NftItemSearchMediumType
    | NftItemSearchSort,
    | 'ITEM_SEARCH_AUCTION_LIVE'
    | 'ITEM_SEARCH_AUCTION_SOLD'
    | 'ITEM_SEARCH_AUCTION_UPCOMING'
    | 'ITEM_SEARCH_MARKET_PRIMARY'
    | 'ITEM_SEARCH_MARKET_SECONDARY'
    | 'ITEM_SEARCH_MEDIUM_STATIC_IMAGE'
    | 'ITEM_SEARCH_MEDIUM_GIF'
    | 'ITEM_SEARCH_MEDIUM_VIDEO'
    | 'ITEM_SEARCH_MEDIUM_3D_MODEL'
    | 'ITEM_SEARCH_MEDIUM_HTML'
    | 'ITEM_SEARCH_SORT_AUCTION_ACTIVITY'
    | 'ITEM_SEARCH_SORT_NEWEST'
    | 'ITEM_SEARCH_SORT_PRICE_HIGH_TO_LOW'
    | 'ITEM_SEARCH_SORT_PRICE_LOW_TO_HIGH'
>;

export type FilterShape = {
    auctionStatus: NftItemSearchAuctionStatus | null;
    marketType: NftItemSearchAuctionMarketType | null;
    mediumTypes: NftItemSearchMediumType[] | null;
    sortBy: NftItemSearchSort | null;
};

export type FilterQueryInput = {
    auctionStatuses: NftItemSearchAuctionStatus[] | null;
    auctionMarketTypes: NftItemSearchAuctionMarketType[] | null;
    mediumTypes: NftItemSearchMediumType[] | null;
    sortBy: NftItemSearchSort | null;
};

type FilterConfigItem = {
    isArray: boolean;
    facetName: MessageDescriptor;
    urlAlias: string;
    options: Array<{
        label: MessageDescriptor;
        value: AvailableFiltersValues | null;
        urlAlias?: string;
        isVisible: boolean;
    }>;
};

export type FilterConfig = Record<keyof FilterShape, FilterConfigItem>;

export const getFilterConfig = ({
    upcoming,
    secondaryMarket = true,
}: {
    upcoming: boolean;
    secondaryMarket?: boolean;
}): FilterConfig => {
    return {
        auctionStatus: {
            isArray: false,
            facetName: messages.availability,
            urlAlias: 'auctionStatus',
            options: [
                { label: messages.all, value: null, isVisible: true },
                {
                    label: messages.sold,
                    value: 'ITEM_SEARCH_AUCTION_SOLD',
                    urlAlias: 'sold',
                    isVisible: true,
                },
                {
                    label: messages.liveAuction,
                    value: 'ITEM_SEARCH_AUCTION_LIVE',
                    urlAlias: 'live',
                    isVisible: true,
                },
                {
                    label: messages.upcomingAuctions,
                    value: 'ITEM_SEARCH_AUCTION_UPCOMING',
                    urlAlias: 'upcoming',
                    isVisible: upcoming,
                },
            ],
        },
        marketType: {
            isArray: false,
            facetName: messages.market,
            urlAlias: 'market',
            options: secondaryMarket
                ? [
                      { label: messages.all, value: null, isVisible: true },
                      {
                          label: messages.primaryMarket,
                          value: 'ITEM_SEARCH_MARKET_PRIMARY',
                          urlAlias: 'primary',
                          isVisible: true,
                      },
                      {
                          label: messages.secondaryMarket,
                          value: 'ITEM_SEARCH_MARKET_SECONDARY',
                          urlAlias: 'secondary',
                          isVisible: true,
                      },
                  ]
                : [],
        },
        mediumTypes: {
            isArray: true,
            facetName: messages.medium,
            urlAlias: 'medium',
            options: [
                {
                    label: messages.staticImage,
                    value: 'ITEM_SEARCH_MEDIUM_STATIC_IMAGE',
                    urlAlias: 'image',
                    isVisible: true,
                },
                {
                    label: messages.animatedGif,
                    value: 'ITEM_SEARCH_MEDIUM_GIF',
                    urlAlias: 'gif',
                    isVisible: true,
                },
                {
                    label: messages.video,
                    value: 'ITEM_SEARCH_MEDIUM_VIDEO',
                    urlAlias: 'video',
                    isVisible: true,
                },
                {
                    label: messages.threeD,
                    value: 'ITEM_SEARCH_MEDIUM_3D_MODEL',
                    urlAlias: '3d',
                    isVisible: true,
                },
                {
                    label: messages.html,
                    value: 'ITEM_SEARCH_MEDIUM_HTML',
                    urlAlias: 'html',
                    isVisible: true,
                },
            ],
        },
        sortBy: {
            isArray: false,
            facetName: messages.sortBy,
            urlAlias: 'sortBy',
            options: [
                {
                    label: messages.endingSoonest,
                    value: 'ITEM_SEARCH_SORT_AUCTION_ACTIVITY',
                    urlAlias: 'soonest',
                    isVisible: true,
                },
                {
                    label: messages.newest,
                    value: 'ITEM_SEARCH_SORT_NEWEST',
                    urlAlias: 'newest',
                    isVisible: true,
                },
                {
                    label: messages.priceHiToLo,
                    value: 'ITEM_SEARCH_SORT_PRICE_HIGH_TO_LOW',
                    urlAlias: 'hiToLo',
                    isVisible: true,
                },
                {
                    label: messages.priceLoToHi,
                    value: 'ITEM_SEARCH_SORT_PRICE_LOW_TO_HIGH',
                    urlAlias: 'loToHi',
                    isVisible: true,
                },
            ],
        },
    };
};

export const defaultAuctionStatusFilters: NftItemSearchAuctionStatus[] = [
    'ITEM_SEARCH_AUCTION_LIVE',
    'ITEM_SEARCH_AUCTION_SOLD',
    'ITEM_SEARCH_AUCTION_UPCOMING',
];

export const initialFilters: FilterShape = {
    auctionStatus: null,
    marketType: null,
    mediumTypes: null,
    sortBy: null,
};
